import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { trustCopy } from "@/config/branding";
import { getOffer } from "@/config/offers";
import { env } from "@/config/env";
import { MessageScreen } from "@/components/MessageScreen";
import { IntakeFlow } from "./IntakeFlow";
import { buildRuntimeConfig } from "./runtime";

/** Standalone entry for an offer: hero/start screen, then the question flow. */
export function StartScreen() {
  const params = useParams();
  const offerId = params.offerId ?? env.defaultOfferId ?? "";
  const offer = getOffer(offerId);
  const [started, setStarted] = useState(false);

  const runtime = useMemo(
    () => (offer ? buildRuntimeConfig(offer.offerId, "standalone") : null),
    [offer],
  );

  if (!offer || !runtime) {
    return (
      <MessageScreen
        tone="error"
        title="Offer not found"
        description="We couldn't find that intake form. Please check the link and try again."
      />
    );
  }

  if (started) {
    return <IntakeFlow offer={offer} runtime={runtime} />;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-5 py-16">
      <h1 className="text-3xl font-extrabold leading-tight text-ink sm:text-5xl">{offer.title}</h1>
      {offer.subtitle ? (
        <p className="mt-4 text-xl text-ink-soft">{offer.subtitle}</p>
      ) : null}
      {offer.intro ? <p className="mt-4 text-base text-ink-muted">{offer.intro}</p> : null}

      {offer.pricing?.fromPriceLabel ? (
        <p className="mt-6 text-base font-semibold text-brand-700">{offer.pricing.fromPriceLabel}</p>
      ) : null}
      {offer.pricing?.priceRangeLabel ? (
        <p className="mt-1 text-sm text-ink-muted">{offer.pricing.priceRangeLabel}</p>
      ) : null}

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button type="button" className="r1-btn-primary text-lg" onClick={() => setStarted(true)}>
          Get started
        </button>
        {offer.estimatedDurationLabel ? (
          <span className="text-sm text-ink-muted">{offer.estimatedDurationLabel}</span>
        ) : offer.estimatedMinutes ? (
          <span className="text-sm text-ink-muted">
            Takes about {offer.estimatedMinutes} minute{offer.estimatedMinutes === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>

      <ul className="mt-10 flex flex-col gap-2 text-sm text-ink-muted">
        {trustCopy.startBullets.map((line) => (
          <li key={line}>• {line}</li>
        ))}
      </ul>
    </div>
  );
}
